import { NavTab, NavTabContainer, NavTabGroup } from 'components/Nav';
import RedirectRoute from 'components/Router/RedirectRoute';
import { UserRoute } from 'components/Router/UserRoute';
import TabbedHeaderLayout from 'components/TabbedHeaderLayout';
import {
  Sidebar,
  StyledDetailsContainerMobile
} from 'components/TabbedHeaderLayout/Sidebar';
import Tour from 'components/Tour';
import { ACKNOWLEDGEMENTS } from 'config/constants/acknowledgements';
import routeMap from 'config/routes';
import { GetLabelAndViewerQuery } from 'gql/graphql';
import { findInGraphQLError } from 'graphql/helpers/utils';
import { GetLabelAndViewer } from 'graphql/queries/label';
import { viewerLabelSubscriptionsTransform } from 'graphql/transforms';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useBreakpoint from 'hooks/useBreakpoint';
import useCurrentUser from 'hooks/useCurrentUser';
import React from 'react';
import { Redirect, Route, Switch, useLocation, useParams } from 'react-router-dom';
import { createAcknowledgement as createAcknowledgementAction } from 'redux/actions/acknowledgements';
import {
  selectAllAcknowledgementTypes,
  selectAllUserAcknowledgementsFetched
} from 'redux/selectors/acknowledgements';
import ErrorPage, { ERROR_PAGE_STATUS, RouteNotFound } from 'routes/ErrorPage';
import { useQuery } from 'urql';
import LabelArtists from './LabelArtists';
import LabelDetails from './LabelDetails';
import LabelFeedback from './LabelFeedback';
import LabelHeaderNotification from './LabelHeaderNotification';
import LabelReleases from './LabelReleases';
import LabelSubscribers from './LabelSubscribers';
import LabelTracks from './LabelTracks';
import { labelTourSteps } from './tourSteps';

const PROMO_ACKNOWLEDGMENT = ACKNOWLEDGEMENTS.PROMO_POOL_TUTORIAL_LABEL_MANAGERS;

const EMPTY_LABEL: NonNullable<GetLabelAndViewerQuery['label']> = {
  id: '',
  name: '',
  slug: '',
  genres: [],
  description: '',
  activeStartYear: NaN,
  activeEndYear: NaN,
  hasPromoAccess: false,
  logo: {
    url: ''
  },
  viewer: {
    id: '',
    isLabelManager: false,
    hasReleasesOnLabel: false
  }
};

interface LabelRouteProps {
  match: {
    isExact: boolean;
    params: { id: string; name: string };
    path: string;
    url: string;
  };
}

const Label = ({ match }: LabelRouteProps) => {
  const { id: labelId } = useParams<{ id: string }>();
  const location = useLocation();

  const isLargeScreen = useBreakpoint(
    useBreakpoint.BREAKPOINTS.LARGE,
    useBreakpoint.DIRECTIONS.UP
  );

  // Define routes
  const labelRoutes = routeMap.labels;
  const paths = {
    subscriber: match.url + labelRoutes.subscribers,
    feedback: match.url + labelRoutes.feedback,
    tracks: match.url + labelRoutes.tracks,
    artists: match.url + labelRoutes.artists,
    details: match.url + labelRoutes.details,
    releases: match.url + labelRoutes.releases,
    url: match.url,
    // if on base url, we will redirect to the default path
    default: match.url + labelRoutes.tracks
  };

  // In order to display the promo navigation tabs, the user must have promo subscriber access (full or limited)
  // If coming straight to the label feedback page, we need to:
  // 1) load the user
  // 2) load the label to check promo_access flag
  // 3) check if user is label manager or has releases on the label
  // 4) show the feedback page.

  // force loading screen if:
  // 1) user is fetching
  // 2) label is still fetching

  const { user, fetching: isUserFetching } = useCurrentUser();

  const dispatch = useAppDispatch();
  const acknowledgementTypes = useAppSelector<string[]>(state =>
    selectAllAcknowledgementTypes(state)
  );
  const acknowledgementsFetched = useAppSelector(state =>
    selectAllUserAcknowledgementsFetched(state)
  );

  const [{ data, fetching: isLabelFetching, error: labelError }] = useQuery({
    query: GetLabelAndViewer,
    variables: {
      labelId: labelId
    },
    requestPolicy: 'cache-and-network'
  });

  if (labelError) {
    const notFound = findInGraphQLError(labelError, {
      extensionCode: 'NOT_FOUND',
      path: ['label']
    });

    if (notFound) {
      return (
        <ErrorPage
          error={{
            ...ERROR_PAGE_STATUS.NOT_FOUND,
            title: 'Label Not Found',
            testId: Label.TEST_IDS.ERROR_PAGE
          }}
        />
      );
    }
  }

  const label = data?.label || EMPTY_LABEL;
  const labelViewer = label?.viewer || EMPTY_LABEL.viewer;

  const loading =
    isUserFetching ||
    isLabelFetching ||
    // TODO: This is a hack to deal with graphql cache resolving the label to null when viewer data resolves from another query in the app.
    // This should be removed once we switch to a new query/caching tool.
    (!data?.label?.id && !labelError);

  const viewerSubscriptions = viewerLabelSubscriptionsTransform(data);

  // NOTE: admins should be able to access subscriber page in order to add first promo subscriber which triggers
  // promo_access attribute to be set to "full_access"
  const isAdmin = user?.roles.admin;
  const isLabelManager = !!labelViewer?.isLabelManager;
  const hasReleasesOnLabel = !!labelViewer?.hasReleasesOnLabel;
  const canEditLabel = isAdmin || isLabelManager;
  const isProUser = user?.roles.labelManager || user?.roles.artist;
  const hasSubscriberAccess =
    (canEditLabel && label.hasPromoAccess && isLabelManager) || isAdmin;

  const showFeedbackTab =
    (label.hasPromoAccess && (isLabelManager || hasReleasesOnLabel)) || isAdmin;

  const subscribersRouteProps = {
    redirectTo: paths.default,
    isAuthorized: !!hasSubscriberAccess,
    forceLoading: loading
  };
  const defaultRouteProps = { label, labelId, match, isLabelManager };
  const feedbackRouteProps = {
    // we let pro users visit this page just in case this link was shared with them, but they won't see
    // the navigation link
    isAuthorized: isProUser,
    forceLoading: loading,
    routeProps: {
      isAdminUser: isAdmin,
      hasReleasesOnLabel,
      isLabelManager,
      label
    }
  };

  const missingPromoAck = !acknowledgementTypes.includes(PROMO_ACKNOWLEDGMENT);
  const promoPath = [paths.subscriber, paths.feedback].includes(location.pathname);
  const showPromoTour =
    isLabelManager && missingPromoAck && promoPath && acknowledgementsFetched;

  if (showPromoTour) {
    return (
      <Tour
        steps={labelTourSteps}
        backgroundImageSrc="images/promo-background.jpg"
        onFinish={() =>
          dispatch(createAcknowledgementAction(PROMO_ACKNOWLEDGMENT))
            .then(response => {
              console.warn('acknowledgement created', response);
            })
            .catch(e => {
              console.warn('acknowledgement failed', e);
            })
        }
        onFinishText="Let's Go!"
      />
    );
  }

  return (
    <div className="Label__container">
      <TabbedHeaderLayout
        header={{
          entityType: 'label',
          imageUrl: label?.logo?.url,
          title: label?.name,
          titleLabel: 'Music Label',
          loading: loading,
          renderTabs: () => (
            <NavTabContainer>
              <NavTabGroup title="Collection">
                <NavTab title="Tracks" path={paths.tracks} />
                <NavTab title="Releases" path={paths.releases} />
                <NavTab title="Artists" path={paths.artists} />
              </NavTabGroup>

              {(hasSubscriberAccess || showFeedbackTab) && (
                <NavTabGroup title="Promo Pool">
                  {hasSubscriberAccess && (
                    <NavTab title="Subscribers" path={paths.subscriber} />
                  )}
                  {showFeedbackTab && <NavTab title="Feedback" path={paths.feedback} />}
                </NavTabGroup>
              )}

              {!isLargeScreen && (
                <NavTabGroup>
                  <NavTab title="Details" path={paths.details} />
                </NavTabGroup>
              )}
            </NavTabContainer>
          ),
          renderNotification: () => (
            <LabelHeaderNotification
              label={label}
              labelSubscriptions={viewerSubscriptions}
              isLabelManager={isLabelManager}
              hasReleasesOnLabel={hasReleasesOnLabel}
            />
          )
        }}
        renderSidebar={() => {
          return (
            <Sidebar>
              <LabelDetails label={label} loading={loading} truncate />
            </Sidebar>
          );
        }}
        renderMain={() => {
          // NOTE: Screen matches check required to prevent redirects from double firing
          return (
            <Switch>
              <RedirectRoute
                exact
                path={paths.details}
                to={paths.default}
                shouldRedirect={isLargeScreen}
                render={() => (
                  <StyledDetailsContainerMobile>
                    <LabelDetails label={label} loading={loading} />
                  </StyledDetailsContainerMobile>
                )}
              />
              <UserRoute
                exact
                path={paths.subscriber}
                component={LabelSubscribers}
                routeProps={defaultRouteProps}
                {...subscribersRouteProps}
              />
              <UserRoute
                exact
                path={paths.feedback}
                component={LabelFeedback}
                {...feedbackRouteProps}
              />
              <Route
                exact
                path={paths.tracks}
                render={() => (
                  <LabelTracks
                    {...defaultRouteProps}
                    labelId={labelId}
                    label={label}
                    canEditLabel={canEditLabel}
                    loading={loading}
                  />
                )}
              />
              <Redirect
                from="/labels/:id/:name/artists"
                to="/labels/:id/:name/artists/top"
                exact
              />
              <Route path="/labels/:id/:name/artists/:tab" component={LabelArtists} />
              <Route
                exact
                path={paths.releases}
                render={() => (
                  <LabelReleases labelId={labelId} canEditLabel={canEditLabel} />
                )}
              />
              {/* if on root path (i.e. /labels/:id/:name) redirect to 1st tab route (i.e. /labels/:id/:name/subscribers) */}
              <Redirect from="/labels/:id/:name" to={paths.default} />
              <RouteNotFound />
            </Switch>
          );
        }}
      />
    </div>
  );
};

Label.TEST_IDS = {
  ERROR_PAGE: 'Label-ErrorPage',
  HEADER_NOTIFICATION: LabelHeaderNotification.TEST_IDS.CONTAINER,
  HEADER_NOTIFICATION_ACCEPT: LabelHeaderNotification.TEST_IDS.ACCEPT,
  HEADER_NOTIFICATION_DISMISS: LabelHeaderNotification.TEST_IDS.DISMISS,
  TITLE: 'PageHeader-title'
};

export default Label;
